import { dollars, dollarsToCents } from "../../../utils/money";
import {
  flatMappingToColumnMappings,
  columnMappingsToFlatMapping,
  getPreviewValue,
} from "../csvMapping/utils";

const WIRE_FIELDS: API.WireImport.WireField[] = [
  "recipient_name",
  "routing_number",
  "account_number",
  "amount",
  "memo",
  "wire_reason",
  "recipient_street_address",
  "recipient_city",
  "recipient_region_code",
  "recipient_postal_code",
];

const REQUIRED_FIELDS: API.WireImport.WireField[] = WIRE_FIELDS;

/**
 * Convert a flat persisted wire CSV field mapping to a ColumnMapping[].
 * Fields with null column_index are skipped.
 */
export const savedMappingToColumnMappings = (
  mapping: API.WireImport.CSVFieldMapping,
): API.WireImport.ColumnMapping[] => {
  return flatMappingToColumnMappings(mapping, WIRE_FIELDS);
};

/**
 * Convert a ColumnMapping[] back to the flat request format for saving/updating.
 */
export const columnMappingsToSavedMapping = (
  name: string,
  hasHeaderRow: boolean,
  columnMappings: API.WireImport.ColumnMapping[],
): API.WireImport.CSVFieldMappingRequest => {
  const template: API.WireImport.CSVFieldMappingRequest = {
    name,
    has_header_row: hasHeaderRow,
    recipient_name_column_index: null,
    recipient_name_column_header: null,
    routing_number_column_index: null,
    routing_number_column_header: null,
    account_number_column_index: null,
    account_number_column_header: null,
    amount_column_index: null,
    amount_column_header: null,
    memo_column_index: null,
    memo_column_header: null,
    wire_reason_column_index: null,
    wire_reason_column_header: null,
    recipient_street_address_column_index: null,
    recipient_street_address_column_header: null,
    recipient_city_column_index: null,
    recipient_city_column_header: null,
    recipient_region_code_column_index: null,
    recipient_region_code_column_header: null,
    recipient_postal_code_column_index: null,
    recipient_postal_code_column_header: null,
  };
  return columnMappingsToFlatMapping(template, columnMappings);
};

/**
 * Format parsed rows into the submit payload.
 * - Filters out rows without a matched recipient
 * - Filters out rows with non-finite or <= 0 amounts
 * - Filters out rows with missing or blank memos
 * - Converts amount from dollars to cents
 */
export const parsedRowsToSubmitPayload = (
  rows: API.WireImport.CSVParsedRow[],
): API.WireImport.SubmitRow[] => {
  return rows
    .filter((row) => row.recipient !== null)
    .filter((row) => {
      const amount = row.amount ?? 0;
      return Number.isFinite(amount) && amount > 0;
    })
    .filter((row) => Boolean(row.memo?.trim()))
    .map((row) => {
      const amountCents = dollarsToCents(dollars(row.amount!));
      const submitRow: API.WireImport.SubmitRow = {
        recipient_uuid: row.recipient!.id as API.RecipientId,
        amount: amountCents,
        memo: row.memo!,
      };
      if (row.wire_reason) {
        submitRow.wire_reason = row.wire_reason;
      }
      return submitRow;
    });
};

/**
 * Validate that all required fields have been mapped.
 * Returns the list of required fields that are missing from the mappings.
 */
export const getMissingRequiredFields = (
  columnMappings: API.WireImport.ColumnMapping[],
): API.WireImport.WireField[] => {
  const mappedFields = new Set(columnMappings.map((cm) => cm.field));
  return REQUIRED_FIELDS.filter((f) => !mappedFields.has(f));
};

export { getPreviewValue };
